import CopyButton from "@components/buttons/CopyButton";
import LabelRow from "@components/rowcomponents/LabelRow";
import TruncatedCopyValue from "@components/rowcomponents/TruncatedCopyValue";
import { getApiErrorDisplay } from "@services/api/apiError";
import { primeServices } from "@services/apis/Prime";
import axios from "axios";
import { transactionApiServices } from "@services/apis/Transactions";
import {
  GetPrimeTradeDetailsData,
  GetPrimeTradeDetailsResponse,
} from "@services/models/Prime/GetPrimeTradeDetailsModel";
import {
  SettleTransactionRequest,
  SettleTransactionResponse,
} from "@services/models/Transactions/SettleTransactionModel";
import React, { useEffect, useState } from "react";
import webSocketService from "../../services/websocket/WebsocketServices";
import ConstantFunctions from "../../utils/constants_functions";
import useSnackbarMessage from "../../utils/HandleSuccessErrorSnackbars";
import InvertibleDateTime from "./components/InvertibleDateTime";
import InvertibleRate from "./components/InvertibleRate";
import PrimeDetailsActionButton from "./components/PrimeDetailsActionButton";
import PrimeOrderFlowDetailsSkeleton from "./components/PrimeOrderFlowDetailsSkeleton";
import { downloadBrazilPixReceiptPdf } from "./receipts/downloadBrazilPixReceiptPdf";
import { isPrimeTradeCompleted } from "./receipts/isPrimeTradeCompleted";
import { mapPixTransactionToReceipt } from "./receipts/mapPixTransactionToReceipt";

interface PrimeOrderFlowDetailsProps {
  tradeId: string;
}

export const PrimeOrderFlowDetails: React.FC<PrimeOrderFlowDetailsProps> = ({
  tradeId,
}) => {
  const [data, setData] = useState<GetPrimeTradeDetailsData>();
  const [error, setError] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const constFunctions = new ConstantFunctions();
  const [isReceiptLoading, setIsReceiptLoading] = useState<boolean>(false);

  const fetchData = async () => {
    try {
      setIsLoading(true);
      const response: GetPrimeTradeDetailsResponse =
        await primeServices.getPrimeTradeDetails({ tradeId: tradeId });
      setData(response.data);
    } catch (error: any) {
      setError(true);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [tradeId]);

  const handleTradeChangeWebSockets = (
    socketData: GetPrimeTradeDetailsData,
  ) => {
    if (socketData.tradeId.toString() === tradeId) {
      setData(socketData);
    }
  };

  useEffect(() => {
    const initializeWebSocket = async () => {
      try {
        webSocketService.onMessage(
          "admin_trade_details_update",
          true,
          handleTradeChangeWebSockets,
        );
      } catch (error) {
        console.error("WebSocket initialization error:", error);
      }
    };

    initializeWebSocket();
  }, [webSocketService.protectedSocket?.connected]);

  const [isButtonLoading, setIsButtonLoading] = useState<boolean>(false);
  const { handleError, handleSuccess } = useSnackbarMessage();

  const handleSendCallback = async () => {
    try {
      setIsButtonLoading(true);
      const settleTransactionRequest: SettleTransactionRequest = {
        merchantOrderId: data?.MOID,
        merchantId: data?.MID.toString(),
      };

      const response: SettleTransactionResponse =
        await transactionApiServices.settleTransaction(
          settleTransactionRequest,
          "/analyticsDashboard/sendPrimeWebhook",
        );
      handleSuccess({
        message: response.msg || "Transaction settled successfully",
      });
    } catch (error: unknown) {
      if (!axios.isAxiosError(error)) {
        handleError({ message: getApiErrorDisplay(error) });
      }
    } finally {
      setIsButtonLoading(false);
    }
  };

  const handleGenerateReceipt = async () => {
    if (!data?.MOID && !data?.instanceId) {
      handleError({ message: "Missing transaction ID for PIX receipt." });
      return;
    }

    try {
      setIsReceiptLoading(true);
      const pixTransaction = await primeServices.getPixTransaction({
        id: data.MOID || data.instanceId,
      });
      const mapped = mapPixTransactionToReceipt(pixTransaction);
      await downloadBrazilPixReceiptPdf(mapped);
      handleSuccess({ message: "PIX receipt PDF downloaded." });
    } catch (error: unknown) {
      if (!axios.isAxiosError(error)) {
        handleError({ message: getApiErrorDisplay(error) });
      }
    } finally {
      setIsReceiptLoading(false);
    }
  };

  const isINR =
    data && data?.countryCode && data?.countryCode === "IN" ? true : false;
  const isBrazil = data?.countryCode === "BR";
  const canGenerateReceipt = isBrazil && isPrimeTradeCompleted(data);
  const isBuying =
    data?.tradeType === "BUY" ||
    data?.tradeType === "B" ||
    data?.isBuying === 1;
  const hasEvent4 =
    data?.eventDetails?.some((event) => String(event.eventId) === "4") ?? false;
  const tradeAmount = Number(data?.tradeAmount ?? data?.cryptoAmount ?? 0);
  const toReleaseAmount = Number(
    data?.toReleaseAmount ?? data?.releaseAmount ?? 0,
  );
  const fees = isBuying
    ? tradeAmount - toReleaseAmount
    : Number(data?.escrowAmount ?? 0) - tradeAmount;

  const cryptoToFiatRate = data?.rate
    ? isINR
      ? Number(data.rate)
      : 1 / Number(data.rate)
    : undefined;

  if (error) {
    return <div>Fetch error</div>;
  }

  if (isLoading) {
    return <PrimeOrderFlowDetailsSkeleton width="w-full" />;
  }

  return (
    <div
      className="flex flex-col gap-[12px] mt-[20px]"
      style={{ height: "65dvh" }}
    >
      <div className="flex flex-col py-[8px] px-[12px] space-y-[16px] border border-border-subtle bg-bg-base rounded-lg">
        <div className="flex flex-col gap-3">
          <p className="text-text-navbar-active text-[12px] font-semibold underline">
            Merchant Details:
          </p>
          {/* MID | MUID Section */}
          {data?.MUID && (
            <LabelRow
              label="MID | MUID"
              valueComp={
                <div className="flex flex-row gap-[4px] items-center">
                  <span className="text-text-base text-[12px] font-normal">
                    {data?.MID}
                  </span>
                  <CopyButton
                    copyText={data?.MID.toString() ?? ""}
                    copyMessage="Copied to clipboard"
                  />
                  <span className="text-text-base text-[12px] font-normal">
                    |
                  </span>
                  <span className="text-text-base text-[12px] font-normal">
                    {data?.MUID}
                  </span>
                  <CopyButton
                    copyText={data?.MUID.toString() ?? ""}
                    copyMessage="Copied to clipboard"
                  />
                </div>
              }
            />
          )}
          {/* Merchant Info */}
          <LabelRow label="Merchant Name" value={data?.merchantName} />
          {/* Merchant Email */}
          <LabelRow
            label="Merchant Email ID"
            value={data?.merchantEmailId}
            isCopy={!!data?.merchantEmailId}
          />
        </div>
      </div>
      <div className="flex flex-col py-[8px] px-[12px] space-y-[16px] border border-border-subtle bg-bg-base rounded-lg">
        <div className="flex flex-col gap-3">
          <div className="flex flex-row items-center justify-between">
            <p className="text-text-navbar-active text-[12px] font-semibold underline">
              Order Details:
            </p>
            <div
              className={`flex text-[10px] h-[20px] px-[12px] font-bold text-center items-center rounded-lg ${
                data?.tradeType === "BUY"
                  ? "bg-bg-status-green"
                  : "bg-bg-status-red"
              } text-text-base`}
            >
              {data?.tradeType}
            </div>
          </div>
          <LabelRow
            label="Trade ID"
            value={data?.tradeId.toString() ?? ""}
            isCopy={!!data?.tradeId.toString()}
          />
          <LabelRow
            label="MOID"
            value={data?.MOID ?? ""}
            valueComp={
              <TruncatedCopyValue
                value={data?.MOID}
                copyMessage="MOID copied to clipboard"
              />
            }
          />

          {/* Instance ID */}
          <LabelRow
            label="Instance ID"
            value={data?.instanceId ?? ""}
            isCopy={!!data?.instanceId}
            valueComp={
              <div className="flex flex-row gap-[4px] items-center">
                <span className="text-text-base text-[12px] font-normal">
                  {data?.instanceId && data?.instanceId.substring(0, 8)}...
                </span>
                <CopyButton
                  copyText={data?.instanceId ?? ""}
                  copyMessage="Copied to clipboard"
                />
              </div>
            }
          />
         {/* Cx Email */}
         <LabelRow
            label="Cx Email ID"
            value={data?.cxEmailId ?? ""}
            isCopy={!!data?.cxEmailId}
            valueComp={
              <div className="flex flex-row gap-[4px] items-center">
                <span className="text-text-base text-[12px] font-normal">
                  {data?.cxEmailId && data?.cxEmailId.substring(0, 8)}...
                </span>
                {data?.cxEmailId && (
                  <CopyButton
                    copyText={data?.cxEmailId ?? ""}
                    copyMessage="Copied to clipboard"
                  />
                )}
              </div>
            }
          />
          {isINR && (
            <>
              {/* UTR Number */}
              <LabelRow
                label="Cx UTR Number"
                value={data?.utr ?? "- -"}
                isCopy={!!data?.utr}
              />
            </>
          )}
          {/* Trade Date */}
          <InvertibleDateTime value={data?.createdAt} />
          {/* Manual Settlment */}
          {isINR && (
            <>
              <LabelRow
                label="Manually Settled"
                value={data?.manualSettlement === 1 ? "Yes" : "No"}
              />
            </>
          )}
        </div>

        <div className="flex flex-row items-center justify-between">
          <PrimeDetailsActionButton
            label="Send callback"
            icon="send"
            onClick={handleSendCallback}
            loading={isButtonLoading}
          />
          {canGenerateReceipt && (
            <PrimeDetailsActionButton
              label="Receipt"
              icon="receipt_long"
              onClick={handleGenerateReceipt}
              loading={isReceiptLoading}
            />
          )}
        </div>
      </div>

      {isINR && (
        <div className="flex flex-col py-[8px] px-[12px] space-y-[16px] border border-border-subtle bg-bg-base rounded-lg">
          <div className="flex flex-col gap-3">
            <p className="text-text-navbar-active text-[12px] font-semibold underline">
              Cashier Details:
            </p>
            {/* Cashier UTR Number */}
            <LabelRow
              label="Cashier UTR Number"
              value={data?.cashierUTR ?? "- -"}
              isCopy={!!data?.cashierUTR}
            />
            {/* Payment Proof Image */}
            {data?.paymentProofImage && (
              <LabelRow
                label="Payment Proof Image"
                valueComp={
                  <button
                    type="button"
                    className="flex flex-row gap-[4px] items-center cursor-pointer"
                    onClick={() =>
                      window.open(
                        data.paymentProofImage ?? "",
                        "_blank",
                        "noopener,noreferrer",
                      )
                    }
                    title="Open image in new tab"
                  >
                    <img
                      src={data.paymentProofImage}
                      height={12}
                      width={12}
                      alt="Payment proof"
                    />
                  </button>
                }
              />
            )}
          </div>
        </div>
      )}

      <div className="flex flex-col py-[8px] px-[12px] space-y-[12px] border border-border-subtle bg-bg-base rounded-lg">
        <p className="text-text-navbar-active text-[12px] font-semibold underline">
          Trade Details:
        </p>
        {/* Token and Fiat Section */}
        <LabelRow
          label="Token | Fiat"
          valueComp={
            <div className="flex flex-row gap-[4px] items-center">
              <img src={data?.cryptoCurrencyImage} height={12} width={12} />
              <span className="text-text-base text-[12px] font-normal">
                {data?.cryptoCurrencySymbol}
              </span>
              <span className="text-text-base text-[12px] font-normal">|</span>
              <img src={data?.fiatCurrencyImage} height={12} width={12} />
              <span className="text-text-base text-[12px] font-normal">
                {data?.fiatCurrencySymbol}
              </span>
            </div>
          }
        />

        {/* Amount and Escrow */}
        <LabelRow
          label="Amount in VDA | Fiat"
          valueComp={
            <div className="flex flex-row gap-[4px] items-center">
              <span className="text-text-base text-[12px] font-normal">
                {Number(data?.cryptoAmount ?? 0).toFixed(2)}
              </span>
              <img src={data?.cryptoCurrencyImage} height={12} width={12} />
              <span className="text-text-base text-[12px] font-normal">|</span>
              <span className="text-text-base text-[12px] font-normal">
                {Number(data?.fiatAmount ?? 0).toFixed(2)}
              </span>
              <img src={data?.fiatCurrencyImage} height={12} width={12} />
            </div>
          }
        />

        {/* Escrow Amount */}
        {isINR && (
          <LabelRow
            label="Amount to Escrow | Release"
            valueComp={
              <div className="flex flex-row gap-[4px] items-center">
                <span className="text-text-base text-[12px] font-normal">
                  {Number(data?.escrowAmount ?? 0).toFixed(2)}
                </span>
                <img src={data?.cryptoCurrencyImage} height={12} width={12} />
                <span className="text-text-base text-[12px] font-normal">
                  |
                </span>
                <span className="text-text-base text-[12px] font-normal">
                  {Number(data?.releaseAmount ?? 0).toFixed(2)}
                </span>
                <img src={data?.cryptoCurrencyImage} height={12} width={12} />
              </div>
            }
          />
        )}
        {!isINR && (
          <LabelRow
            label={isBuying ? "Amount Credited" : "Amount Debited"}
            valueComp={
              hasEvent4 ? (
                <div className="flex flex-row gap-[4px] items-center">
                  <span className="text-text-base text-[12px] font-normal">
                    {Number(
                      isBuying
                        ? (data?.releaseAmount ?? 0)
                        : (data?.escrowAmount ?? 0),
                    ).toFixed(2)}
                  </span>
                  <img src={data?.cryptoCurrencyImage} height={12} width={12} />
                </div>
              ) : (
                <span className="text-text-base text-[12px] font-normal">
                  –
                </span>
              )
            }
          />
        )}

        {isINR && (
          <>
            {/* Escrow Party */}
            <LabelRow label="Escrow Party" value={data?.escrowParty} />
            {/* Release Party */}
            <LabelRow label="Release Party" value={data?.releaseParty} />
          </>
        )}

        {/* Rate */}
        <LabelRow
          label="Rate"
          valueComp={
            <InvertibleRate
              cryptoToFiatRate={cryptoToFiatRate}
              cryptoImage={data?.cryptoCurrencyImage}
              cryptoSymbol={data?.cryptoCurrencySymbol}
              fiatImage={data?.fiatCurrencyImage}
              fiatSymbol={data?.fiatCurrencySymbol}
            />
          }
        />
        {!isINR && (
          <>
            <LabelRow
              label="Fees"
              valueComp={
                <div className="flex flex-row gap-[4px] items-center">
                  <span className="text-text-base text-[12px] font-normal">
                    {fees.toFixed(2)}
                  </span>
                  <img src={data?.cryptoCurrencyImage} height={12} width={12} />
                </div>
              }
            />
          </>
        )}
      </div>
    </div>
  );
};

export default PrimeOrderFlowDetails;
